import { useEffect, useState, useRef, useCallback } from "react"
import TextField from "@mui/material/TextField"
import CircularProgress from "@mui/material/CircularProgress"
import IconButton from "@mui/material/IconButton"
import RefreshIcon from "@mui/icons-material/Refresh"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import AppApi from "../utils/AppApi"
import moment from "moment"
import { Timeline } from "vis-timeline"
import { DataSet } from "vis-data"

const JOB_COLORS = {
  F: "#bbdefb",
  B: "#c8e6c9",
  S: "#ffe0b2",
  L: "#e1bee7",
}

const JOB_LABELS = {
  F: "Front Haul",
  B: "Back Haul",
  S: "Short Range",
  L: "Long Range",
  G: "Garage",
}

const GARAGE_COLOR = "#ef9a9a"

const ReportTruckGantt = () => {
  const [data, setData] = useState([])
  const [value, setValue] = useState(new Date())
  const [isLoading, setIsLoading] = useState(false)
  const containerRef = useRef(null)
  const timelineRef = useRef(null)

  const getData = useCallback(async () => {
    setIsLoading(true)
    try {
      let result = await AppApi.post("/rpt_driver", {
        viewMonth: moment(value).format("YYMM"),
        includeOverlapping: true , 
      })
      setIsLoading(false)
      setData(result.data)
    } catch (error) {
      setIsLoading(false)
      console.log(error.message)
    }
  }, [value])

  useEffect(() => {
    getData()
  }, [getData])

  useEffect(() => {
    if (!containerRef.current || data.length === 0) return

    if (timelineRef.current) {
      timelineRef.current.destroy()
      timelineRef.current = null
    }

    const groups = new DataSet(
      data.map((d) => ({ id: d.truck_no, content: d.truck_no }))
    )

    const historyItems = data.flatMap((d) =>
      d.history.filter((h) => h.start_time).map((h, i) => {
        const start = new Date(h.start_time)
        const end = h.end_time ? new Date(h.end_time) : new Date()
        const durMin = moment(end).diff(moment(start), "minutes")
        const hrs = Math.floor(durMin / 60)
        const mins = durMin % 60
        const color = JOB_COLORS[h.job_type] || "#888"

        return {
          id: `${d.truck_no}-h-${i}`,
          group: d.truck_no,
          content: `<span style="font-size:11px;color:#000;font-weight:bold">${h.dn} [${h.job_type}]</span>`,
          start,
          end,
          type: "range",
          className: `job-${h.job_type}`,
          style: `background-color:${color};border-color:${color};`,
          title: [
            `<b>DN:</b> ${h.dn}`,
            `<b>Driver:</b> ${h.driver}`,
            `<b>Job:</b> ${JOB_LABELS[h.job_type] || h.job_type}`,
            `<b>Start:</b> ${moment(start).format("DD/MM/YY HH:mm")}`,
            `<b>End:</b> ${moment(end).format("DD/MM/YY HH:mm")}`,
            `<b>Duration:</b> ${hrs}h ${mins}m`,
          ].join("<br/>"),
        }
      })
    )

    const garageItems = data.flatMap((d) =>
      (d.garage_visits || []).filter((g) => g.entry_time).map((g, i) => {
        const start = new Date(g.entry_time)
        const end = g.exit_time ? new Date(g.exit_time) : new Date()
        const durMin = moment(end).diff(moment(start), "minutes")
        const hrs = Math.floor(durMin / 60)
        const mins = durMin % 60

        return {
          id: `${d.truck_no}-g-${i}`,
          group: d.truck_no,
          content: `<span style="font-size:11px;color:#b71c1c;font-weight:bold">&#9881; ${g.garage_name}</span>`,
          start,
          end,
          type: "range",
          style: `background-color:${GARAGE_COLOR};border-color:#e53935;`,
          title: [
            `<b>Garage:</b> ${g.garage_name} (${g.garage_code})`,
            `<b>Entry:</b> ${moment(start).format("DD/MM/YY HH:mm")}`,
            `<b>Exit:</b> ${g.exit_time ? moment(end).format("DD/MM/YY HH:mm") : "ยังอยู่ในอู่"}`,
            `<b>Duration:</b> ${hrs}h ${mins}m`,
          ].join("<br/>"),
        }
      })
    )

    const items = new DataSet([...historyItems, ...garageItems])

    const options = {
      width: "100%",
      height: "80vh",
      horizontalScroll: true,
      stack: true,
      showCurrentTime: true,
      orientation: { axis: "top", item: "top" },
      zoomMin: 1000 * 60 * 60,
      zoomMax: 1000 * 60 * 60 * 24 * 90,
      groupOrder: "id",
      margin: { item: { horizontal: 2, vertical: 3 } },
      tooltip: { followMouse: true, overflowMethod: "cap" },
      start: moment(value).startOf("month").toDate(),
      end: moment(value).startOf("month").add(3, "days").toDate(),
      min: moment(value).subtract(1, "month").startOf("month").toDate(),
      max: moment(value).add(1, "month").endOf("month").toDate(),
    }

    timelineRef.current = new Timeline(
      containerRef.current,
      items,
      groups,
      options
    )

    return () => {
      if (timelineRef.current) {
        timelineRef.current.destroy()
        timelineRef.current = null
      }
    }
  }, [data, value])

  return (
    <div
      style={{
        height: "93vh",
        overflowY: "scroll",
        paddingLeft: "5px",
        paddingTop: "20px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: "5px",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            views={["year", "month"]}
            label="Year and Month"
            minDate={new Date("2022-02-01")}
            maxDate={new Date("2050-12-31")}
            value={value}
            onChange={(newValue) => setValue(newValue)}
            renderInput={(params) => <TextField {...params} helperText={null} />}
          />
        </LocalizationProvider>
        <IconButton onClick={getData} disabled={isLoading} size="small">
          <RefreshIcon />
        </IconButton>
        {isLoading && <CircularProgress size={24} />}
        <span style={{ fontSize: "13px", color: "#666" }}>
          Scroll to zoom · Drag to pan
        </span>
      </div>

      <h2 style={{ margin: "5px 0" }}>Truck Gantt Chart</h2>

      <div
        style={{
          display: "flex",
          gap: "12px",
          marginBottom: "8px",
          fontSize: "13px",
        }}
      >
        {Object.entries(JOB_LABELS).map(([key, label]) => (
          <span
            key={key}
            style={{ display: "flex", alignItems: "center", gap: "4px" }}
          >
            <span
              style={{
                display: "inline-block",
                width: "14px",
                height: "14px",
                backgroundColor: key === "G" ? GARAGE_COLOR : JOB_COLORS[key],
                borderRadius: "3px",
              }}
            />
            {label}
          </span>
        ))}
      </div>

      <div ref={containerRef} />
    </div>
  )
}

export default ReportTruckGantt
